import { nanoid } from "nanoid";
import { useState } from "react";
import { useRxData } from "rxdb-hooks";
import {
  type EpocusConsentDocument,
  type EpocusConsentDocumentItem,
  RXDB_EPOCUS_CONSENT_DOCUMENT_COLLECTION_NAME,
} from "../../../../../global-providers/rxdb-provider/collections/epocus-consent-document";
import {
  type EpocusConsentAudienceValue,
  type EpocusConsentResponseValue,
  type EpocusPatientConsent,
  RXDB_EPOCUS_PATIENT_CONSENT_COLLECTION_NAME,
  useRxCollectionEpocusPatientConsent,
} from "../../../../../global-providers/rxdb-provider/collections/epocus-patient-consent";
import { useUser } from "../../../../../global-providers/user-provider/provider";
import { useEpocusLivePrivacyPolicyQuery } from "../../../_lib/consent/__generated__/epocus-consent-operations.gql";
import { PatientConsentDialog } from "./patient-consent-dialog";

export const PatientConsentGate: FC<{
  encounterId: string;
  onCancel: () => void;
}> = ({ encounterId, onCancel }) => {
  const user = useUser();
  const patientConsentCollection = useRxCollectionEpocusPatientConsent();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAudience, setSelectedAudience] =
    useState<EpocusConsentAudienceValue | null>(null);

  const { result: consentDocuments } = useRxData<EpocusConsentDocument>(
    RXDB_EPOCUS_CONSENT_DOCUMENT_COLLECTION_NAME,
    (collection) => collection.find(),
  );
  const liveDocument = consentDocuments.reduce<EpocusConsentDocument | null>(
    (latest, doc) => (!latest || doc.version > latest.version ? doc : latest),
    null,
  );

  const { result: existingConsentRows } = useRxData<EpocusPatientConsent>(
    RXDB_EPOCUS_PATIENT_CONSENT_COLLECTION_NAME,
    (collection) => collection.find({ selector: { encounterId } }),
  );

  const documentItems = liveDocument?.items ?? [];

  const chosenAudience =
    existingConsentRows[0]?.audience ?? selectedAudience ?? null;
  const audienceItems = chosenAudience
    ? documentItems.filter((item) => item.audience === chosenAudience)
    : [];
  const audience = audienceItems.length > 0 ? chosenAudience : null;

  const termsDocumentItem =
    audienceItems.find((item) => item.kind === "TERMS_OF_REGISTRATION") ?? null;
  const allQuestionItems = audienceItems
    .filter((item) => item.kind !== "TERMS_OF_REGISTRATION")
    .sort((a, b) => a.ordinal - b.ordinal);

  // Only ask what isn't already on record for this encounter — otherwise a
  // consent-document version bump (ADR 0006 treats content as append-only
  // versioned, never edited in place) would re-ask every question and insert
  // duplicate append-only rows for kinds already answered.
  const answeredKinds = new Set(existingConsentRows.map((row) => row.kind));
  const questionItems = allQuestionItems.filter(
    (item) => !answeredKinds.has(item.kind),
  );
  const termsItem = answeredKinds.has("TERMS_OF_REGISTRATION")
    ? null
    : termsDocumentItem;
  const needsAudience = audience === null && documentItems.length > 0;
  const willShowDialog =
    needsAudience || questionItems.length > 0 || termsItem !== null;

  const { data: privacyPolicyData } = useEpocusLivePrivacyPolicyQuery({
    skip: !willShowDialog,
  });

  if (!willShowDialog || !liveDocument) return null;

  const handleSubmit = async (
    answersByItemId: Record<string, EpocusConsentResponseValue>,
  ) => {
    if (!patientConsentCollection || !audience) return;
    setIsSubmitting(true);
    try {
      const now = new Date().toISOString();
      const meta = {
        createdAt: now,
        createdByUserId: user.id,
        updatedAt: now,
        updatedByUserId: user.id,
      };
      const insertRow = (
        kind: EpocusConsentDocumentItem["kind"],
        response: EpocusConsentResponseValue,
      ) =>
        patientConsentCollection.insert({
          id: nanoid(),
          encounterId,
          elephantPatientIdentityValue: null,
          audience,
          kind,
          response,
          consentDocumentId: liveDocument.id,
          privacyPolicyId:
            privacyPolicyData?.epocusLivePrivacyPolicy?.id ?? null,
          programId: null,
          meta,
        });

      await Promise.all([
        ...questionItems.map((item) =>
          insertRow(item.kind, answersByItemId[item.id]),
        ),
        ...(termsItem ? [insertRow(termsItem.kind, "YES")] : []),
      ]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PatientConsentDialog
      open
      audience={audience}
      onAudienceChange={setSelectedAudience}
      termsItem={termsItem}
      questionItems={questionItems}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      onCancel={onCancel}
    />
  );
};
